import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { upperFirst } from 'lodash';
import { RpmAccount } from '../../../../store/RpmAccount';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { TextBlock } from '../../../Atoms/Text';
import { SearchBox } from '../../Search/Search';
import { EntityType, SearchResult } from '../../../../store/Search';
import { Association, createAssociations } from '../../../../store/Ticket';
import { Asset } from '../../../../store/Asset';
import { ProvisionalAsset } from '../../../../store/ProvisionalAsset';
import { fetchSupplierMapsByRpmAcctSupplierId } from '../../../../store/SupplierMap';

export interface NewReferenceTicketDialogParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  association?: Association;
  objectData: Asset | RpmAccount | ProvisionalAsset;
  objectType: 'asset' | 'provisionalAsset' | 'rpmAccount' | 'customer';
}

const modObjectType = {
  provisionalAsset: 'provisional_asset',
  rpmAccount: 'rmpAccount'
};

export const NewReferenceTicketDialog = ({ open, setOpen, objectData, objectType }: NewReferenceTicketDialogParams) => {
  const [loading, setLoading] = useState(false);
  const objectDataId = objectType == 'rpmAccount' ? objectData.accountId : (objectData as ProvisionalAsset | Asset).id;
  const [filters, setFilters] = useState<Record<string, string>>(() => ({
    [`no${upperFirst(objectType)}Id`]: String(objectDataId)
  }));

  const handleSelect = async (result: SearchResult) => {
    setLoading(true);
    await createAssociations([
      {
        body: {
          entityId: String(objectDataId),
          ticketId: result.id
        },
        type: (modObjectType[objectType as keyof typeof modObjectType] || objectType) as Association['type']
      }
    ]);
    setLoading(false);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    let cancelled = false;
    if (objectType === 'asset' && 'supplierId' in objectData && objectData.supplierId) {
      setFilters((prev) => ({ ...prev, supplierId: objectData.supplierId as string }));
    }
    if (objectType === 'provisionalAsset' && 'supplier' in objectData && objectData.supplier) {
      setFilters((prev) => ({ ...prev, supplierId: objectData.supplier as string }));
    }
    const maybeAddRpmSupplierFilter = async () => {
      if (objectType !== 'rpmAccount' || !('supplierId' in objectData) || !objectData.supplierId) return;
      const supplierMaps = await fetchSupplierMapsByRpmAcctSupplierId(objectData.supplierId);
      const supplierMapId = supplierMaps?.[0]?.supplier_id ?? null;
      if (!cancelled && supplierMapId) {
        setFilters((prev) => ({ ...prev, supplierId: supplierMapId }));
      }
    };
    void maybeAddRpmSupplierFilter();
    return () => {
      cancelled = true;
    };
  }, [objectData, objectType, objectDataId]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          sx: { overflowY: 'visible' }
        }
      }}
      fullWidth
      maxWidth="xs"
      data-testid="add-reference-ticket-dialog">
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Add Reference to Ticket
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, overflowY: 'visible' }}>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <div className="space-y-3 justify-center mt-3">
            <TextBlock color="grey6">Ticket</TextBlock>
            <SearchBox
              handleSelect={handleSelect}
              searchEntityType={EntityType.TICKET}
              inputString=""
              fullWidth
              placeholder=""
              filters={filters}
            />
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
